// src/App.tsx
import React from 'react';
import { ConfigProvider } from 'antd';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import CRMLayout from './components/Layout/CRMLayout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Login from './components/Auth/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CalendarPage from './pages/Calendar';
import ConsultingPage from './pages/Consulting';
import Settings from './pages/Settings';
import Unauthorized from './pages/Unauthorized';
import CustomerView from './pages/CustomerView';
import { AuthProvider, useAuth } from './context/AuthContext';
import Team from './pages/Team';
import it from 'antd/lib/locale/it_IT';
import Billing from './pages/Billing';
import ProfilePage from './pages/Profile';
import AnalyticsPage from './pages/Analytics';
import Contacts from './pages/Contacts';
import LandingPages from './pages/LandingPages';
import LandingPublic from './pages/public/LandingPublic';
import LandingBuilderPage from './pages/landingBuilder/LandingBuilderPage';
import PrivacyPolicy from './pages/public/PrivacyPolicy';

// ======= CUSTOMER APP PAGES =======
import CustomerHome from './pages/customerApp/customerHome';
import CustomerNews from './pages/customerApp/News';
import NewsDetail from './pages/customerApp/NewsDetail';
import TimelineActivities from './pages/customerApp/TimelineActivities';
import NewsManagement from './pages/NewsManager';
import CustomerLandingPages from './pages/customerApp/CustomerLandingPages';

// Transizioni pagina + reset scroll
function PageTransition({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  React.useEffect(() => {
    // evita che il browser ripristini la posizione di scroll precedente
    if ('scrollRestoration' in window.history) {
      try {
        window.history.scrollRestoration = 'manual';
      } catch {}
    }

    // reset scroll finestra
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

      // reset anche eventuali contenitori scrollabili della UI
      const containers: (Element | null)[] = [
        document.querySelector('.ant-layout-content'),
        document.querySelector('.container-v2'),
        document.scrollingElement,
        document.documentElement,
      ];
      containers.forEach((el) => {
        const node = el as HTMLElement | null;
        if (node && typeof node.scrollTop === 'number') {
          try { node.scrollTop = 0; } catch {}
        }
      });
    });
  }, [location.pathname]);

  return (
    <div key={location.key} className="page-transition">
      {children}
    </div>
  );
}

const theme = {
  token: {
    colorPrimary: '#007AFF',
    colorBgContainer: '#ffffff',
    colorBgLayout: '#f8f9fa',
    borderRadius: 12,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
};

const RESERVED_SLUGS = new Set(
  [
    '',
    'login',
    'unauthorized',
    'home',
    'news',
    'activities',
    'customers',
    'calendar',
    'consulting',
    'team',
    'contacts',
    'settings',
    'billing',
    'profile',
    'analytics',
    'landing-pages',
    'newsmanager',
    'api',
    'g',
    'p',
    'privacy',
    'assets',
    'static',
    'favicon.ico',
  ].map((value) => value.toLowerCase()),
);

function LayoutWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isSettings = location.pathname === '/settings';
  return (
    <CRMLayout isSettings={isSettings}>
      <PageTransition>{children}</PageTransition>
    </CRMLayout>
  );
}

function CRMApp() {
  const { user } = useAuth();
  const role = React.useMemo(
    () => String(user?.role || '').toUpperCase().replace(/^ROLE_/, ''),
    [user?.role],
  );
  const isCustomer = role === 'CUSTOMER';

  return (
    <ProtectedRoute>
      {isCustomer ? (
        <Navigate to="/home" replace />
      ) : (
        <LayoutWrapper>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/newsmanager" element={<NewsManagement />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/customers/:id" element={<CustomerView />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/consulting" element={<ConsultingPage />} />
            <Route path="/team" element={<Team />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/landing-pages" element={<LandingPages />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </LayoutWrapper>
      )}
    </ProtectedRoute>
  );
}

function LandingOrApp() {
  const location = useLocation();
  const slug = React.useMemo(() => {
    const [first] = location.pathname.split('/').filter(Boolean);
    return (first || '').toLowerCase();
  }, [location.pathname]);

  if (slug && !RESERVED_SLUGS.has(slug)) {
    return <LandingPublic slug={slug} />;
  }
  return <CRMApp />;
}

function AppRoutes() {
  const { isAuthenticated, user } = useAuth();
  const role = String(user?.role || '').toUpperCase().replace(/^ROLE_/, '');
  const isCustomer = role === 'CUSTOMER';
  const homePath = isCustomer ? '/home' : '/';

  return (
    <Routes>
      {/* Login: se già autenticato vai alla tua home */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to={homePath} replace /> : <Login />}
      />

      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* ======= PORTALE CUSTOMER (fuori da LayoutWrapper) ======= */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            {isCustomer ? (
              <PageTransition>
                <CustomerHome />
              </PageTransition>
            ) : (
              <Navigate to="/" replace />
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/news"
        element={
          <ProtectedRoute>
            {isCustomer ? (
              <PageTransition>
                <CustomerNews />
              </PageTransition>
            ) : (
              <Navigate to="/" replace />
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/news/:id"
        element={
          <ProtectedRoute>
            {isCustomer ? (
              <PageTransition>
                <NewsDetail />
              </PageTransition>
            ) : (
              <Navigate to="/" replace />
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/activities"
        element={
          <ProtectedRoute>
            {isCustomer ? (
              <PageTransition>
                <TimelineActivities />
              </PageTransition>
            ) : (
              <Navigate to="/" replace />
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/customer/landing-pages"
        element={
          <ProtectedRoute>
            {isCustomer ? (
              <PageTransition>
                <CustomerLandingPages />
              </PageTransition>
            ) : (
              <Navigate to="/" replace />
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/landing-builder/new"
        element={
          <ProtectedRoute>
            {isCustomer ? <Navigate to="/" replace /> : <LandingBuilderPage />}
          </ProtectedRoute>
        }
      />

      <Route
        path="/landing-builder/:id"
        element={
          <ProtectedRoute>
            {isCustomer ? <Navigate to="/" replace /> : <LandingBuilderPage />}
          </ProtectedRoute>
        }
      />

      <Route
        path="/privacy"
        element={
          <PageTransition>
            <PrivacyPolicy />
          </PageTransition>
        }
      />

      {/* ======= CRM INTERNO (con LayoutWrapper) ======= */}
      <Route path="/*" element={<LandingOrApp />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ConfigProvider theme={theme} locale={it}>
        <Router>
          <AppRoutes />
        </Router>
      </ConfigProvider>
    </AuthProvider>
  );
}
